import { gql } from '@apollo/client';

export const PARTNER = gql`
    fragment Partner on PartnerNode {
        id
        name
        displayName
        logoPath
        options
    }
`;

export const GET_PARTNERS = gql`
    query getPartners {
        partners {
            ...Partner
        }
    }
    ${PARTNER}
`;

export const CONFIGURED_PARTNER = gql`
    fragment ConfiguredPartner on ConfiguredPartnerNode {
        id
        name
        displayName
        attributionPartnerName
        icon
        appSitesCount
        linksCount
        lastModified
        hasAgencyConfig
        missingAgenciesNames
    }
`;

export const GET_CONFIGURED_PARTNERS = gql`
    query getConfiguredPartners($appFilter: String, $agencyFilter: String) {
        configuredPartners(appFilter: $appFilter, agencyFilter: $agencyFilter) {
            ...ConfiguredPartner
        }
    }
    ${CONFIGURED_PARTNER}
`;

export const EVENT = gql`
    fragment Event on EventNode {
        id
        eventName
        displayName
        revenueEvent
    }
`;

export const GET_COUNTRIES = gql`
    query getCountries {
        countries {
            isoCode
            displayName
        }
    }
`;

export const GET_POSTBACK_FILTERS = gql`
    query getPostbackFilters {
        filters {
            name
            displayName
            options {
                name
                displayName
            }
            type
        }
    }
`;

export const GET_EVENTS = gql`
    query getEvents($siteId: ID!, $partnerId: ID) {
        events(siteId: $siteId, partnerId: $partnerId) {
            ...Event
        }
    }
    ${EVENT}
`;

export const GET_PARTNER = gql`
    query getPartner($partnerId: ID!) {
        partner(id: $partnerId) {
            id
            name
            baseSourceName
            displayName
            logoPath
            sendAllInstalls
            sendAllEvents
            viewthroughSupport
            installSupport
            reengagementSupport
            ipProbabilisticSupport
            fraudPostbackSupport
            attributionWindowsSupport
            description
            instructions
            guideLink
            postbackTemplates {
                type
                platform
                clickWindow
                installWindow
                parameters
                templateUri
            }
            parameters {
                id
                name
                type
                description
                label
                predefinedValues
                required
                regex
                regexDescription
                displayIndex
                displayType
            }
            privacyParameters {
                id
                name
                type
                description
                label
                predefinedValues
                required
                regex
                displayIndex
                displayType
            }
            accountsSupport
            sanAgencyIdsSupport
        }
    }
`;

export const ATTRIBUTION_POSTBACK = gql`
    fragment AttributionPostback on AttributionPostbackNode {
        sendAll
        sendViewThrough
        campaignRegex
        postbackUri
        customByUser
        filters
    }
`;

export const EVENT_POSTBACK = gql`
    fragment EventPostback on EventPostbackNode {
        eventId
        eventName
        sendAll
        installWindow
        clickWindow
        parameters
        sendRevenue
        campaignRegex
        postbackUri
        customByUser
        filters
    }
`;

export const WINDOWS = gql`
    fragment Windows on WindowsNode {
        installClickWindowDeterministic
        installClickWindowProbabilistic
        installViewWindowDeterministic
        installViewWindowProbabilistic
        reengClickWindowDeterministic
        reengClickWindowProbabilistic
        reengViewWindowDeterministic
        reengViewWindowProbabilistic
        reengInactivityWindow
        ctvViewWindow
        ctvPrioritizationWindow
    }
`;

export const POSTBACKS = gql`
    fragment Postbacks on PostbacksNode {
        install {
            ...AttributionPostback
        }
        reengagement {
            ...AttributionPostback
        }
        customEvents {
            ...EventPostback
        }
        revenueEvents {
            ...EventPostback
        }
        fraudEnabled
    }
    ${ATTRIBUTION_POSTBACK}
    ${EVENT_POSTBACK}
`;

export const GET_PARTNER_CONFIGURATIONS_FOR_PARTNER = gql`
    query getPartnerConfigurationsForPartner($partnerId: Int!) {
        partnerConfigurations(partnerId: $partnerId) {
            siteId
            agencyId
            sanAgencyIds
            appName
            icon
            os
            longname
            lastModified
            ldsOptions {
                restrictLds
                ldsDefault
                anonymizePostbacks
            }
            agencyName
            isCustom
            postbacks {
                install {
                    sendAll
                }
                reengagement {
                    sendAll
                }
                customEvents {
                    eventName
                    sendAll
                }
                revenueEvents {
                    eventName
                    sendAll
                }
            }
            windows {
                installClickWindowDeterministic
            }
        }
    }
`;

export const GET_PARTNER_CONFIGURATION_FOR_APP = gql`
    query getPartnerConfigurationForApp($partnerId: Int!, $longname: String!, $platform: String!, $agencyId: String) {
        partnerConfiguration(partnerId: $partnerId, longname: $longname, platform: $platform, agencyId: $agencyId) {
            siteId
            agencyId
            sanAgencyIds
            ldsOptions {
                restrictLds
                ldsDefault
                anonymizePostbacks
            }
            isCustom
            customDescription
            parameters
            accounts {
                loginId
                loginData {
                    qqNumber
                }
                loginAccounts {
                    accountId
                    accountName
                    enabled
                }
            }
            postbacks {
                ...Postbacks
            }
            geoRestrictions {
                allowedCountries
                excludedCountries
            }
            attributionReturnRate
            windows {
                ...Windows
            }
        }
    }
    ${POSTBACKS}
    ${WINDOWS}
`;

export const GET_ORGANIZATION_OPTIONS = gql`
    query getOrganizationOptions {
        isAdmin
        ldsSupported
        geoRestrictionsSupported
        trackerCampaignNameFilterSupported
        advancedPostbackEditingSupported
        flexiblePostbackSupported
        zeroInactivityWindowSupported
        sansFlexiblePostbackReadOnlyMode
        sanPartnersForAgencySupported
        sanPartnersViewOnlyForAgencySupported
        attributionReturnRateSupported
        eulasAgreements
        postbackFiltersSupported
        showAttributionPartnerName
    }
`;

export const DELETE_CONFIGURATION = gql`
    mutation deleteConfiguration($partnerId: Int!, $siteId: Int!, $agencyId: String) {
        deletePartnerConfiguration(partnerId: $partnerId, siteId: $siteId, agencyId: $agencyId) {
            ok
            error
        }
    }
`;

export const SAVE_CONFIGURATION = gql`
    mutation saveConfiguration($partnerConfiguration: SavedPartnerConfiguration!) {
        savePartnerConfiguration(partnerConfiguration: $partnerConfiguration) {
            ok
            error
        }
    }
`;

export const SAVE_EULA_AGREEMENT = gql`
    mutation saveEula($partnerName: String!) {
        saveEulaAgreement(partnerName: $partnerName) {
            ok
            error
        }
    }
`;

export const GET_PARTNER_APP_DEFAULT_WINDOWS = gql`
    query getPartnerAppDefaultWindows($partnerId: Int!, $longname: String!, $platform: String!) {
        partnerAppDefaultWindows(partnerId: $partnerId, longname: $longname, platform: $platform) {
            installClickWindowId
            installClickWindowIp
            installViewWindowId
            installViewWindowIp
            reengagementClickWindowId
            reengagementClickWindowIp
            reengagementViewWindowId
            reengagementViewWindowIp
            reengagementInactivityWindow
            ctvViewWindow
            ctvPrioritizationWindow
        }
    }
`;

export const GET_PARTNER_ACCOUNTS = gql`
    query getPartnerAccounts($partnerId: ID!) {
        partnerAccounts(partnerId: $partnerId) {
            loginId
            loginData {
                qqNumber
            }
            loginAccounts {
                accountId
                accountName
                enabled
            }
            loginError
        }
    }
`;

export const SAVE_PARTNER_ACCOUNT = gql`
    mutation savePartnerAccount(
        $partnerId: Int!
        $loginId: String!
        $loginData: [LoginDataInput!]!
        $loginAccounts: [LoginAccountInput!]
    ) {
        savePartnerAccount(
            partnerId: $partnerId
            loginId: $loginId
            loginData: $loginData
            loginAccounts: $loginAccounts
        ) {
            ok
            error
        }
    }
`;

export const GET_USER_FAVOURITE_PARTNERS = gql`
    query getFavouritePartners {
        favouritePartners
    }
`;

export const UPDATE_USER_FAVOURITE_PARTNER = gql`
    mutation updateUserFavouritePartner($partnerId: Int!, $favourite: Boolean!) {
        updateUserFavouritePartner(partnerId: $partnerId, favourite: $favourite) {
            ok
            error
        }
    }
`;
